// assets/custom-megamenu.js
(function () {
  'use strict';

  function debounce(fn, delay) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
  }

  class MegaNav extends HTMLElement {
    connectedCallback() {
      this._triggers      = Array.from(this.querySelectorAll('[data-megamenu]'));
      this._backdrop      = this.querySelector('.hbk-mega-nav__backdrop');
      this._activeTrigger = null;
      this._activePanel   = null;
      this._openTimer     = null;
      this._closeTimer    = null;
      this._isReduced     = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this._subItems       = Array.from(this.querySelectorAll('.hbk-mega-nav__item--has-sub'));
      this._activeSubItem  = null;
      this._subOpenTimer   = null;
      this._subCloseTimer  = null;

      if (!this._triggers.length && !this._subItems.length) return;

      this._scrollHandler = debounce(() => {
        if (this._activePanel) this._updatePanelTop();
      }, 8);
      this._resizeHandler = debounce(() => this._updatePanelTop(), 100);

      window.addEventListener('scroll', this._scrollHandler, { passive: true });
      window.addEventListener('resize', this._resizeHandler);

      this._triggers.forEach(trigger => {
        trigger.addEventListener('mouseenter', () => this._onEnter(trigger));
        trigger.addEventListener('mouseleave', () => this._onLeave());
        trigger.addEventListener('click',      (e) => this._onClick(e, trigger));
        trigger.addEventListener('keydown',    (e) => this._onTriggerKey(e, trigger));
      });

      this._subItems.forEach(item => {
        const trigger  = item.querySelector('.hbk-mega-nav__sub-trigger');
        const dropdown = item.querySelector('.hbk-mega-nav__sub-dropdown');
        if (!trigger || !dropdown) return;

        item.addEventListener('mouseenter', () => {
          clearTimeout(this._subCloseTimer);
          clearTimeout(this._subOpenTimer);
          this._subOpenTimer = setTimeout(() => this._openSub(item, trigger, dropdown), 150);
        });
        item.addEventListener('mouseleave', () => {
          clearTimeout(this._subOpenTimer);
          this._subCloseTimer = setTimeout(() => this._closeSub(item, trigger, dropdown), 150);
        });

        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          clearTimeout(this._subOpenTimer);
          clearTimeout(this._subCloseTimer);
          if (trigger.getAttribute('aria-expanded') === 'true') {
            this._closeSub(item, trigger, dropdown);
          } else {
            this._openSub(item, trigger, dropdown);
          }
        });

        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (trigger.getAttribute('aria-expanded') === 'true') {
              this._closeSub(item, trigger, dropdown);
            } else {
              this._openSub(item, trigger, dropdown);
              setTimeout(() => dropdown.querySelector('a[href], button')?.focus(), 16);
            }
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (trigger.getAttribute('aria-expanded') !== 'true') {
              this._openSub(item, trigger, dropdown);
            }
            setTimeout(() => dropdown.querySelector('a[href], button')?.focus(), 16);
          } else if (e.key === 'Escape') {
            this._closeSub(item, trigger, dropdown);
            trigger.focus();
          }
        });
      });

      this.addEventListener('mouseleave', () => this._scheduleClose());
      this.addEventListener('mouseenter', () => clearTimeout(this._closeTimer));

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          if (this._activePanel) {
            this._close();
            this._activeTrigger?.focus();
          } else if (this._activeSubItem) {
            const t = this._activeSubItem.querySelector('.hbk-mega-nav__sub-trigger');
            this._closeAllSubs();
            t?.focus();
          }
        }
      });

      document.addEventListener('click', (e) => {
        if (this._activePanel && !this.contains(e.target) && !e.target.closest('.hbk-mega-nav__panel')) {
          this._close();
        }
        if (this._activeSubItem && !e.target.closest('.hbk-mega-nav__item--has-sub')) {
          this._closeAllSubs();
        }
      });

      if (this._backdrop) {
        this._backdrop.addEventListener('click', () => this._close());
      }

      this._updatePanelTop();
    }

    disconnectedCallback() {
      clearTimeout(this._openTimer);
      clearTimeout(this._closeTimer);
      clearTimeout(this._subOpenTimer);
      clearTimeout(this._subCloseTimer);
      clearTimeout(this._subSelectTimer);
      clearTimeout(this._backdropHideTimer);
      window.removeEventListener('scroll', this._scrollHandler);
      window.removeEventListener('resize', this._resizeHandler);
    }

    // ── Trigger interaction ───────────────────────────────
    _onEnter(trigger) {
      clearTimeout(this._closeTimer);
      clearTimeout(this._openTimer);
      this._openTimer = setTimeout(() => this._open(trigger), 200);
    }

    _onLeave() {
      clearTimeout(this._openTimer);
    }

    _onClick(e, trigger) {
      e.preventDefault();
      clearTimeout(this._openTimer);
      clearTimeout(this._closeTimer);
      if (trigger.getAttribute('aria-expanded') === 'true') {
        this._close();
      } else {
        this._open(trigger);
      }
    }

    _onTriggerKey(e, trigger) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._onClick(e, trigger);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (trigger.getAttribute('aria-expanded') !== 'true') this._open(trigger);
        document.getElementById(trigger.dataset.megamenu)
          ?.querySelector('a[href], button:not([disabled])')?.focus();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const idx = this._triggers.indexOf(trigger);
        this._triggers[(idx + 1) % this._triggers.length]?.focus();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const idx = this._triggers.indexOf(trigger);
        this._triggers[(idx - 1 + this._triggers.length) % this._triggers.length]?.focus();
      }
    }

    // ── Simple-link sub-dropdown ──────────────────────────
    _openSub(item, trigger, dropdown) {
      this._closeAllSubs();
      if (this._activePanel) this._close();
      this._activeSubItem = item;
      clearTimeout(dropdown._hideTimer);
      trigger.setAttribute('aria-expanded', 'true');
      dropdown.removeAttribute('hidden');
      if (this._isReduced) {
        dropdown.classList.add('is-open');
      } else {
        requestAnimationFrame(() => requestAnimationFrame(() => dropdown.classList.add('is-open')));
      }
    }

    _closeSub(item, trigger, dropdown) {
      trigger.setAttribute('aria-expanded', 'false');
      dropdown.classList.remove('is-open');
      const dur = this._isReduced ? 0 : 150;
      clearTimeout(dropdown._hideTimer);
      dropdown._hideTimer = setTimeout(() => {
        if (!dropdown.classList.contains('is-open')) dropdown.setAttribute('hidden', '');
      }, dur);
      if (this._activeSubItem === item) this._activeSubItem = null;
    }

    _closeAllSubs() {
      this._subItems?.forEach(item => {
        const t = item.querySelector('.hbk-mega-nav__sub-trigger');
        const d = item.querySelector('.hbk-mega-nav__sub-dropdown');
        if (t && d && t.getAttribute('aria-expanded') === 'true') {
          this._closeSub(item, t, d);
        }
      });
    }

    // ── Open / close ──────────────────────────────────────
    _showPanel(panel, trigger) {
      if (this._activePanel && this._activePanel !== panel) {
        this._closePanel(this._activeTrigger, this._activePanel, true);
      }

      // Cancel any pending hide so a reopen never gets re-hidden mid-flight.
      clearTimeout(panel._hideTimer);
      clearTimeout(this._backdropHideTimer);

      this._activeTrigger = trigger || null;
      this._activePanel   = panel;
      this._updatePanelTop();

      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('hidden');

      if (panel.dataset.animation === 'none' || this._isReduced) {
        panel.classList.add('is-open');
      } else {
        requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-open')));
      }

      if (this._backdrop) {
        this._backdrop.removeAttribute('hidden');
        requestAnimationFrame(() => requestAnimationFrame(() => {
          this._backdrop.classList.add('is-visible');
        }));
      }
    }

    // Hover-open path for mega_panel triggers.
    _open(trigger) {
      const panel = document.getElementById(trigger.dataset.megamenu);
      if (!panel) return;

      this._closeAllSubs();
      this._showPanel(panel, trigger);
      this._setupFilterListeners(panel);

      if (panel.dataset.hasSubs === 'true') {
        // Sub-collection navigation lives in the left column; scope to the first sub.
        this._setupSubListListeners(panel);
        const firstSub = panel.querySelector('[data-sub-select]');
        if (firstSub) {
          this._selectSub(panel, firstSub);
        } else {
          panel._activeCollHandle = panel.dataset.collection;
          panel._activeCollUrl    = panel.dataset.collectionUrl;
          this._activateDefaultFilter(panel);
        }
      } else {
        panel._activeCollHandle = panel.dataset.collection;
        panel._activeCollUrl    = panel.dataset.collectionUrl;
        this._activateDefaultFilter(panel);
      }
    }

    // ── Sub-collection navigation (in-panel left column) ──
    _setupSubListListeners(panel) {
      if (panel._subListReady) return;
      panel._subListReady = true;

      panel.querySelectorAll('[data-sub-select]').forEach(btn => {
        const selectNow = () => this._selectSub(panel, btn);
        btn.addEventListener('mouseenter', () => {
          clearTimeout(this._subSelectTimer);
          this._subSelectTimer = setTimeout(selectNow, 80);
        });
        btn.addEventListener('mouseleave', () => clearTimeout(this._subSelectTimer));
        btn.addEventListener('focus', selectNow);
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          clearTimeout(this._subSelectTimer);
          selectNow();
        });
      });
    }

    _selectSub(panel, btn) {
      panel.querySelectorAll('[data-sub-select]').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      panel._activeCollHandle = btn.dataset.collHandle || panel.dataset.collection;
      panel._activeCollUrl    = btn.dataset.collUrl    || panel.dataset.collectionUrl;

      const resultsLink = panel.querySelector('.hbk-mega-nav__results-link');
      if (resultsLink) resultsLink.href = panel._activeCollUrl || '#';

      const first = panel.querySelector('[data-filter-value]');
      if (first) {
        this._activateFilter(panel, first);
      } else {
        const titleEl = panel.querySelector('.hbk-mega-nav__results-title');
        if (titleEl) titleEl.textContent = btn.dataset.collLabel || '';
        this._showAllProducts(panel);
      }
    }

    _scheduleClose() {
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => this._close(), 150);
    }

    _close() {
      if (!this._activePanel) return;
      this._closePanel(this._activeTrigger, this._activePanel, false);
      this._activeTrigger = null;
      this._activePanel   = null;

      if (this._backdrop) {
        this._backdrop.classList.remove('is-visible');
        const dur = this._isReduced ? 0 : 200;
        clearTimeout(this._backdropHideTimer);
        this._backdropHideTimer = setTimeout(() => {
          if (!this._activePanel) this._backdrop.setAttribute('hidden', '');
        }, dur);
      }
    }

    _closePanel(trigger, panel, immediate) {
      if (!panel) return;
      trigger?.setAttribute('aria-expanded', 'false');
      panel.classList.remove('is-open');
      this._resetPanelState(panel);

      clearTimeout(panel._hideTimer);
      if (immediate || this._isReduced) {
        panel.setAttribute('hidden', '');
        return;
      }

      const dur = parseInt(getComputedStyle(panel).getPropertyValue('--panel-duration').trim()) || 200;
      panel._hideTimer = setTimeout(() => {
        if (!panel.classList.contains('is-open')) panel.setAttribute('hidden', '');
      }, dur);
    }

    // ── Positioning ───────────────────────────────────────
    _updatePanelTop() {
      const header = document.querySelector('.section-header');
      if (!header) return;
      const top = header.getBoundingClientRect().bottom;
      this.querySelectorAll('.hbk-mega-nav__panel').forEach(p => { p.style.top = top + 'px'; });
      if (this._backdrop) this._backdrop.style.top = top + 'px';
    }

    // ── Panel state reset ─────────────────────────────────
    _resetPanelState(panel) {
      panel.querySelectorAll('[data-filter-value]').forEach(b => b.classList.remove('is-active'));
      panel.querySelectorAll('[data-sub-select]').forEach(b => b.classList.remove('is-active'));

      panel._activeCollHandle = panel.dataset.collection;
      panel._activeCollUrl    = panel.dataset.collectionUrl;

      const grid = panel.querySelector('.hbk-mega-nav__products-grid');
      if (grid) grid.innerHTML = '';

      const titleEl = panel.querySelector('.hbk-mega-nav__results-title');
      if (titleEl) titleEl.textContent = '';

      const resultsLink = panel.querySelector('.hbk-mega-nav__results-link');
      if (resultsLink) resultsLink.href = panel.dataset.collectionUrl || '#';
    }

    _showAllProducts(panel) {
      const handle = panel._activeCollHandle || panel.dataset.collection;
      const pool   = panel.querySelector(`.hbk-mega-nav__product-pool[data-pool-for="${handle}"]`);
      const grid   = panel.querySelector('.hbk-mega-nav__products-grid');
      if (!grid) return;

      const items = pool ? Array.from(pool.querySelectorAll('.hbk-mega-nav__product-item')) : [];
      grid.innerHTML = '';
      if (items.length === 0) {
        grid.innerHTML = '<p class="hbk-mm-results__empty">No products found.</p>';
      } else {
        items.slice(0, 6).forEach(item => {
          const card = item.querySelector('.hbk-mm-card');
          if (card) grid.appendChild(card.cloneNode(true));
        });
      }
    }

    // ── Filter slots ──────────────────────────────────────
    _setupFilterListeners(panel) {
      if (panel._filtersReady) return;
      panel._filtersReady = true;

      panel.querySelectorAll('[data-filter-value]').forEach(btn => {
        btn.addEventListener('mouseenter', () => this._activateFilter(panel, btn));
        btn.addEventListener('focus',      () => this._activateFilter(panel, btn));
        btn.addEventListener('click', () => {
          const activeUrl = panel._activeCollUrl || panel.dataset.collectionUrl || '';
          if (!activeUrl) return;
          const ns    = btn.dataset.filterNs    || '';
          const key   = btn.dataset.filterKey   || '';
          const value = btn.dataset.filterValue || '';
          // No metafield/tag value → "show all" slot: go to the collection unfiltered.
          if (!value) {
            window.location.href = activeUrl;
            return;
          }
          const param = (!ns || ns === 'tag')
            ? `filter.p.tag=${encodeURIComponent(value)}`
            : `filter.p.m.${ns}.${key}=${encodeURIComponent(value)}`;
          window.location.href = `${activeUrl}?${param}`;
        });
      });
    }

    _activateDefaultFilter(panel) {
      const first = panel.querySelector('[data-filter-value]');
      if (first) this._activateFilter(panel, first);
    }

    _activateFilter(panel, btn) {
      panel.querySelectorAll('[data-filter-value]').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      const activeHandle = panel._activeCollHandle || panel.dataset.collection;
      const activeUrl    = panel._activeCollUrl    || panel.dataset.collectionUrl || '';
      const ns           = btn.dataset.filterNs;
      const key          = btn.dataset.filterKey;
      const value        = btn.dataset.filterValue;
      const label        = btn.dataset.filterLabel || value;
      const filterIndex  = btn.dataset.filterIndex;

      // A slot with no value is a "show all" slot — no filter param, just the collection.
      const filterParam = !value
        ? ''
        : (!ns || ns === 'tag')
          ? `filter.p.tag=${encodeURIComponent(value)}`
          : `filter.p.m.${ns}.${key}=${encodeURIComponent(value)}`;

      const titleEl = panel.querySelector('.hbk-mega-nav__results-title');
      if (titleEl) titleEl.textContent = label;

      const seeAllEl = panel.querySelector('.hbk-mega-nav__results-link');
      if (seeAllEl && activeUrl) {
        seeAllEl.href = filterParam ? `${activeUrl}?${filterParam}` : activeUrl;
      }

      this._filterProducts(panel, filterIndex, value, ns);
    }

    _filterProducts(panel, filterIndex, filterValue, ns) {
      const handle = panel._activeCollHandle || panel.dataset.collection;
      const pool   = panel.querySelector(`.hbk-mega-nav__product-pool[data-pool-for="${handle}"]`);
      const grid   = panel.querySelector('.hbk-mega-nav__products-grid');
      if (!grid) return;

      const needle = (filterValue || '').toLowerCase();
      const items  = pool ? Array.from(pool.querySelectorAll('.hbk-mega-nav__product-item')) : [];

      let matches = [];
      if (!needle) {
        matches = items;
      } else {
        const attr = `data-fi-${filterIndex}`;
        matches = items.filter(item => {
          const stored = (item.getAttribute(attr) || '').toLowerCase();
          if (!stored) return false;
          return ns === 'tag'
            ? stored.split('|').some(t => t.trim() === needle)
            : stored === needle;
        });
      }

      grid.innerHTML = '';
      if (matches.length === 0) {
        grid.innerHTML = '<p class="hbk-mm-results__empty">No products found.</p>';
      } else {
        matches.slice(0, 6).forEach(item => {
          const card = item.querySelector('.hbk-mm-card');
          if (card) grid.appendChild(card.cloneNode(true));
        });
      }
    }
  }

  customElements.define('mega-nav', MegaNav);

  document.addEventListener('shopify:section:load', (e) => {
    const nav = e.target.querySelector('mega-nav');
    if (nav) nav._updatePanelTop?.();
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const nav = e.target.querySelector('mega-nav');
    if (nav) nav._close?.();
  });

})();
